"use client";

import { useAnalytics } from "@analytics";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocaleLink, useLocalePathname } from "@i18n/routing";
import { useNewsletterSignupMutation } from "@marketing/home/lib/api";
import { MARKETING_EVENTS } from "@repo/analytics";
import type { PricingClickedData } from "@repo/analytics";
import { config } from "@repo/config";
import { Logo } from "@shared/components/Logo";
import { Alert, AlertDescription, AlertTitle } from "@ui/components/alert";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { CheckCircleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
	email: z.string().email(),
});
type FormValues = z.infer<typeof formSchema>;

export function Footer() {
	const t = useTranslations();
	const newsletterSignupMutation = useNewsletterSignupMutation();
	const localePathname = useLocalePathname();
	const { trackEvent } = useAnalytics();

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
	});

	const handlePricingClick = () => {
		trackEvent(MARKETING_EVENTS.PRICING_CLICKED, {
			source: "footer",
			location: localePathname,
		} as PricingClickedData);
	};

	const onSubmit = form.handleSubmit(async ({ email }) => {
		try {
			await newsletterSignupMutation.mutateAsync({ email });
		} catch {
			form.setError("email", {
				message: t("newsletter.hints.error.message"),
			});
		}
	});

	return (
		<footer className="border-t py-12 text-foreground/60 text-sm">
			<div className="container">
				<div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
					<div className="col-span-2 sm:col-span-3 lg:col-span-2">
						<Logo className="mb-4 opacity-70 grayscale" />
						<p className="text-foreground/60 text-sm max-w-md">
							{t("marketing.footer.description")}
						</p>
					</div>

					{/* Product (Track / Score / Optimize) + Free Tools + Solutions */}
					<div className="flex flex-col gap-2">
						<h4 className="font-semibold text-foreground mb-1">
							{t("common.menu.nav.groups.track")}
						</h4>
						<LocaleLink
							href="/ai-visibility-report"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiVisibilityReport",
							)}
						</LocaleLink>
						<LocaleLink
							href="/prompt-tracking"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.promptTracking")}
						</LocaleLink>
						<LocaleLink
							href="/person-tracking"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.personTracking")}
						</LocaleLink>
						<LocaleLink
							href="/location-tracking"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.locationTracking")}
						</LocaleLink>
						<LocaleLink
							href="/product-tracking"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.productTracking")}
						</LocaleLink>
						<LocaleLink
							href="/category-tracking"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.categoryTracking")}
						</LocaleLink>
						<LocaleLink
							href="/ai-traffic-analytics"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiTrafficAnalytics",
							)}
						</LocaleLink>

						<h4 className="font-semibold text-foreground mt-3">
							{t("common.menu.nav.groups.score")}
						</h4>
						<LocaleLink
							href="/citation-intelligence"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.citationIntelligence")}
						</LocaleLink>
						<LocaleLink
							href="/ai-placement-value-score"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiPlacementValueScore",
							)}
						</LocaleLink>
						<LocaleLink
							href="/ai-placement-quality-score"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiPlacementQualityScore",
							)}
						</LocaleLink>

						<h4 className="font-semibold text-foreground mt-3">
							{t("common.menu.nav.groups.optimize")}
						</h4>
						<LocaleLink
							href="/citation-optimizer"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.citationOptimizer",
							)}
						</LocaleLink>
						<LocaleLink
							href="/ai-readiness-audit"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiReadinessAudit",
							)}
						</LocaleLink>
						<LocaleLink
							href="/faq-generation"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.faqGeneration",
							)}
						</LocaleLink>
						<LocaleLink
							href="/mcp-server"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.mcpServer")}
						</LocaleLink>

						<h4 className="font-semibold text-foreground mt-3">
							{t("common.menu.nav.groups.prTeams")}
						</h4>
						<LocaleLink
							href="/media-list"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.mediaLists")}
						</LocaleLink>
						<LocaleLink
							href="/coverage-report"
							className="block hover:text-foreground"
						>
							{t("common.menu.products.coverageReports")}
						</LocaleLink>

						<h4 className="font-semibold text-foreground mt-3">
							{t(
								"marketing.footer.sections.product.subheadings.freeTools",
							)}
						</h4>
						<LocaleLink
							href="/ai-access-checker"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiAccessChecker",
							)}
						</LocaleLink>
						<LocaleLink
							href="/common-crawl-rank-checker"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.commonCrawlRankChecker",
							)}
						</LocaleLink>
						<LocaleLink
							href="/is-your-ai-visibility-data-reliable"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.aiVisibilityCalculator",
							)}
						</LocaleLink>
						<LocaleLink
							href="/free-ai-visibility-prompt-generator"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.freeAiVisibilityPromptGenerator",
							)}
						</LocaleLink>
						<LocaleLink
							href="/personal-ai-visibility-report"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.personalAiVisibilityReport",
							)}
						</LocaleLink>
						<LocaleLink
							href="/ai-analytics-live"
							className="block hover:text-foreground"
						>
							{t(
								"common.menu.nav.freeTools.aiAnalyticsLive.label",
							)}
						</LocaleLink>

						<h4 className="font-semibold text-foreground mt-3">
							{t("common.menu.nav.solutions")}
						</h4>
						<LocaleLink
							href="/for-agencies"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.forAgencies",
							)}
						</LocaleLink>
						<LocaleLink
							href="/for-pr-firms"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.forPRFirms",
							)}
						</LocaleLink>
						<LocaleLink
							href="/services"
							className="block hover:text-foreground"
						>
							{t("common.menu.nav.solutionsItems.services")}
						</LocaleLink>
					</div>

					{/* Resources */}
					<div className="flex flex-col gap-2">
						<h4 className="font-semibold text-foreground mb-3">
							{t("marketing.footer.sections.resources.title")}
						</h4>
						<LocaleLink
							href="/demo"
							className="block hover:text-foreground"
						>
							{t("marketing.footer.sections.product.links.demo")}
						</LocaleLink>
						<LocaleLink
							href="/pricing"
							onClick={handlePricingClick}
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.pricing",
							)}
						</LocaleLink>
						<LocaleLink
							href="/compare"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.compare",
							)}
						</LocaleLink>
						<LocaleLink
							href="/docs"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.documentation",
							)}
						</LocaleLink>
						<LocaleLink
							href="/integrations"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.product.links.integrations",
							)}
						</LocaleLink>
						<LocaleLink
							href="/blog"
							className="block hover:text-foreground"
						>
							{t("marketing.footer.sections.product.links.blog")}
						</LocaleLink>
						<LocaleLink
							href="/bots"
							className="block hover:text-foreground"
						>
							{t("marketing.footer.sections.product.links.bots")}
						</LocaleLink>

						{/* Methodology */}
						<h4 className="font-semibold text-foreground mt-3">
							{t("marketing.footer.sections.methodology.title")}
						</h4>
						<LocaleLink
							href="/docs/methodology/ai-visibility-data-collection"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.methodology.links.dataCollection",
							)}
						</LocaleLink>
						<LocaleLink
							href="/docs/methodology/ai-placement-value-score"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.methodology.links.aipvs",
							)}
						</LocaleLink>
						<LocaleLink
							href="/docs/methodology/ai-placement-quality-score"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.methodology.links.pqs",
							)}
						</LocaleLink>
						<LocaleLink
							href="/docs/methodology/citation-optimizer"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.methodology.links.citationOptimizer",
							)}
						</LocaleLink>

						{/* Company */}
						<h4 className="font-semibold text-foreground mt-3">
							{t("marketing.footer.sections.company.title")}
						</h4>
						{config.contactForm.enabled && (
							<LocaleLink
								href="/contact"
								className="block hover:text-foreground"
							>
								{t(
									"marketing.footer.sections.company.links.contact",
								)}
							</LocaleLink>
						)}
						<LocaleLink
							href="/demo"
							className="block hover:text-foreground"
						>
							{t("marketing.footer.sections.product.links.demo")}
						</LocaleLink>
						<a
							href="https://press-kit.spyglasses.io"
							target="_blank"
							rel="noopener noreferrer"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.company.links.pressKit",
							)}
						</a>
						<a
							href="https://brand-book.spyglasses.io"
							target="_blank"
							rel="noopener noreferrer"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.company.links.brandBook",
							)}
						</a>
					</div>

					{/* Legal */}
					<div className="flex flex-col gap-2">
						<h4 className="font-semibold text-foreground mb-3">
							{t("marketing.footer.sections.legal.title")}
						</h4>
						<LocaleLink
							href="/legal/privacy-policy"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.legal.links.privacyPolicy",
							)}
						</LocaleLink>
						<LocaleLink
							href="/legal/terms"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.legal.links.termsOfService",
							)}
						</LocaleLink>
						<LocaleLink
							href="/legal/subprocessors"
							className="block hover:text-foreground"
						>
							{t(
								"marketing.footer.sections.legal.links.subprocessors",
							)}
						</LocaleLink>
					</div>
				</div>

				<div className="mt-8 pt-8 border-t">
					<div className="grid grid-cols-1 gap-6 lg:grid-cols-2 items-center">
						{/* Anchor for "sign up again" on the newsletter-confirm page. */}
						<div id="newsletter">
							<h4 className="font-semibold text-foreground mb-2">
								{t("marketing.footer.newsletter.title")}
							</h4>
							<p className="text-foreground/60 text-sm mb-4">
								{t("marketing.footer.newsletter.description")}
							</p>

							{form.formState.isSubmitSuccessful ? (
								<Alert variant="success" className="max-w-md">
									<CheckCircleIcon className="size-4" />
									<AlertTitle className="text-sm">
										{t("newsletter.hints.success.title")}
									</AlertTitle>
									<AlertDescription className="text-xs">
										{t("newsletter.hints.success.message")}
									</AlertDescription>
								</Alert>
							) : (
								<form onSubmit={onSubmit} className="max-w-md">
									<div className="flex items-start gap-2">
										<div className="flex-1">
											<Input
												type="email"
												required
												placeholder={t(
													"newsletter.email",
												)}
												className="text-sm"
												{...form.register("email")}
											/>
											{form.formState.errors.email && (
												<p className="mt-1 text-destructive text-xs">
													{
														form.formState.errors
															.email.message
													}
												</p>
											)}
										</div>
										<Button
											type="submit"
											size="sm"
											loading={
												form.formState.isSubmitting
											}
										>
											{t("newsletter.submit")}
										</Button>
									</div>
								</form>
							)}
						</div>

						<div className="lg:text-right">
							<p className="text-foreground/40 text-xs">
								{t("marketing.footer.copyright", {
									year: new Date().getFullYear(),
								})}
							</p>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}
